import React, { useState, useEffect } from 'react';
import {
  Sun,
  Cloud,
  CloudSun,
  CloudRain,
  CloudDrizzle,
  Snowflake,
  CloudLightning,
} from 'lucide-react';

interface WeatherData {
  temp: number;
  condition: string;
  icon: string;
  location: string;
  useCelsius: boolean;
}

const WeatherIcon: React.FC<{ icon: string }> = ({ icon }) => {
  const iconMap: Record<
    string,
    React.ComponentType<{ size?: number; className?: string }>
  > = {
    sun: Sun,
    cloud: Cloud,
    'cloud-sun': CloudSun,
    'cloud-rain': CloudRain,
    'cloud-showers-heavy': CloudDrizzle,
    snowflake: Snowflake,
    'cloud-bolt': CloudLightning,
  };

  const IconComponent = iconMap[icon] || Sun;

  return <IconComponent size={16} className="text-gray-600" />;
};

const shouldUseCelsius = (lat: number, lon: number): boolean => {
  // Countries that use Fahrenheit: US, Bahamas, Belize, Cayman Islands, Palau
  // Check if coordinates are in US (approximate bounding box)
  if (lat >= 24 && lat <= 50 && lon >= -125 && lon <= -66) {
    return false; // Likely US
  }
  return true; // Rest of world uses Celsius
};

const getWeatherCondition = (code: number): string => {
  if (code === 0) return 'Clear';
  if (code <= 3) return 'Partly Cloudy';
  if (code <= 48) return 'Foggy';
  if (code <= 67) return 'Rainy';
  if (code <= 77) return 'Snowy';
  if (code <= 82) return 'Rainy';
  if (code <= 86) return 'Snowy';
  return 'Stormy';
};

const getWeatherIcon = (code: number): string => {
  if (code === 0) return 'sun';
  if (code <= 3) return 'cloud-sun';
  if (code <= 48) return 'cloud';
  if (code <= 67) return 'cloud-rain';
  if (code <= 77) return 'snowflake';
  if (code <= 82) return 'cloud-showers-heavy';
  if (code <= 86) return 'snowflake';
  return 'cloud-bolt';
};

const formatTime = (
  date: Date
): { hours: string; minutes: string; period: string } => {
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const [time, period] = timeStr.split(' ');
  const [hours, minutes] = time.split(':');
  return { hours, minutes, period };
};

const WeatherTime: React.FC = () => {
  const [time, setTime] = useState(new Date());
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Fetch weather data
  useEffect(() => {
    const fetchWeatherByCoords = async (latitude: number, longitude: number) => {
      try {
        const useCelsius = shouldUseCelsius(latitude, longitude);
        const tempUnit = useCelsius ? 'celsius' : 'fahrenheit';

        const weatherResponse = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=${tempUnit}&timezone=auto`
        );
        const weatherData = await weatherResponse.json();

        const geoResponse = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
        );
        const geoData = await geoResponse.json();

        const countryCode =
          geoData.address?.country_code?.toUpperCase() || 'XX';
        const city =
          geoData.address?.city ||
          geoData.address?.town ||
          geoData.address?.village ||
          '';

        let locationInitials = countryCode;
        if (
          countryCode === 'US' ||
          countryCode === 'CA' ||
          countryCode === 'AU' ||
          countryCode === 'CN' ||
          countryCode === 'IN'
        ) {
          locationInitials = city
            ? city
                .split(' ')
                .map((word: string) => word[0])
                .join('')
                .substring(0, 2)
                .toUpperCase()
            : countryCode;
        }

        setWeather({
          temp: Math.round(weatherData.current.temperature_2m),
          condition: getWeatherCondition(weatherData.current.weather_code),
          icon: getWeatherIcon(weatherData.current.weather_code),
          location: locationInitials,
          useCelsius,
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching weather:', error);
        setLoading(false);
      }
    };

    const fetchWeatherByIP = async () => {
      try {
        // Use ipapi.co for IP-based geolocation (free, no API key needed)
        const ipResponse = await fetch('https://ipapi.co/json/');
        const ipData = await ipResponse.json();

        if (ipData.latitude && ipData.longitude) {
          await fetchWeatherByCoords(ipData.latitude, ipData.longitude);
        } else {
          throw new Error('No location data from IP');
        }
      } catch (error) {
        console.error('Error fetching location by IP:', error);
        // Fallback to Singapore (1.3521° N, 103.8198° E)
        await fetchWeatherByCoords(1.3521, 103.8198);
      }
    };

    const handleGeolocationSuccess = async (position: GeolocationPosition) => {
      const { latitude, longitude } = position.coords;
      await fetchWeatherByCoords(latitude, longitude);
    };

    const handleGeolocationError = () => {
      // If geolocation fails or is blocked, fall back to IP-based location
      fetchWeatherByIP();
    };

    // Try browser geolocation first (more accurate), fall back to IP-based
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        handleGeolocationSuccess,
        handleGeolocationError,
        { timeout: 5000 } // 5 second timeout
      );
    } else {
      // No geolocation support, use IP-based location
      fetchWeatherByIP();
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 ml-3 pl-3 border-l border-gray-300">
        <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  const { hours, minutes, period } = formatTime(time);

  return (
    <div className="flex items-center gap-2 ml-3 pl-3 border-l border-gray-300">
      {weather && (
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <span className="font-medium">{weather.location}</span>
          <WeatherIcon icon={weather.icon} />
          <span className="font-medium">
            {weather.temp}°{weather.useCelsius ? 'C' : 'F'}
          </span>
        </div>
      )}
      <div className="text-sm text-gray-600 font-medium flex items-center">
        {hours}
        <span className="animate-pulse-colon">:</span>
        {minutes} {period}
      </div>
      <style>{`
        @keyframes pulse-colon {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .animate-pulse-colon {
          animation: pulse-colon 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default WeatherTime;
